import { isNullOrUndefined } from '../../../isNullOrUndefined';
import { CareMattersRiderForm } from '../calcCareMattersCoverage';
import { CareEnvironment, CarePhase } from '../../../appModel';
import { DAYS_IN_MONTH } from './sharedConstants';

// Universal policy-config readers. Strategies that need a different
// resolution rule can reimplement locally rather than import.

// Max daily benefit across all four care environments, treating unset fields
// as 0. Used both for pool sizing and as the fallback for sparse policies
// (legacy/imported rows that populate only one of the four daily fields —
// common on CM II uploads where the four are equal). Mirrors the legacy
// max-benefit fallback in calcFundingSourceDerivatives.ts.
export function getMaxDailyBenefitAmount(ltcPolicy: PolicyFundingSource): number {
  const home = ltcPolicy.dailyHomeBenefitAmount ?? 0;
  const al = ltcPolicy.dailyAssistedLivingBenefitAmount ?? 0;
  const nh = ltcPolicy.dailyNursingHomeBenefitAmount ?? 0;
  const il = ltcPolicy.dailyIndependentLivingBenefitAmount ?? 0;
  return Math.max(home, al, nh, il);
}

export function getMaxMonthlyBenefitAmount(ltcPolicy: PolicyFundingSource): number {
  return getMaxDailyBenefitAmount(ltcPolicy) * DAYS_IN_MONTH;
}

// Reimbursement policies do not cover Independent Living, so shop-insurance
// reimbursement policies have their IL daily benefit zeroed out in
// projections regardless of the stored field value. This predicate is the
// single source of truth for that carve-out — used by the projection
// strategies, the legacy path in calcFundingSourceDerivatives.ts, and the
// client (form shim + policy-details warning) so display and calculation
// never disagree. Accepts a loose shape because client form state can carry
// `addedViaShopInsurance` as the string 'true'.
export function isIndependentLivingBenefitZeroedOut(ltcPolicy: {
  isIndemnityPolicyPayment?: boolean | null;
  addedViaShopInsurance?: boolean | string | null;
}): boolean {
  const addedViaShopInsurance =
    ltcPolicy.addedViaShopInsurance === true || ltcPolicy.addedViaShopInsurance === 'true';
  return addedViaShopInsurance && !ltcPolicy.isIndemnityPolicyPayment;
}

export function getDailyBenefitAmountForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  // An explicitly-set value (including 0) is authoritative for the
  // environment — an explicit 0 means "not covered here" and is honored. A
  // null/undefined field means "not populated on import": legacy and CM II
  // rows commonly carry only one of the four daily fields, so we fall back to
  // the max populated value rather than projecting $0 for an otherwise-covered
  // home/AL/IL phase. Mirrors the legacy max-benefit fallback.
  const resolve = (configured: number | null | undefined): number =>
    configured ?? getMaxDailyBenefitAmount(ltcPolicy);

  switch (careEnvironment) {
    case CareEnvironment.home:
      return resolve(ltcPolicy.dailyHomeBenefitAmount);
    case CareEnvironment.assistedLiving:
      return resolve(ltcPolicy.dailyAssistedLivingBenefitAmount);
    case CareEnvironment.fullCareFacility:
      return resolve(ltcPolicy.dailyNursingHomeBenefitAmount);
    case CareEnvironment.independentLiving:
      // Independent Living care is priced and covered as HOME care — the care
      // portion is home-equivalent, and room & board is out-of-pocket and
      // handled separately (`facts.roomAndBoardCost`). So IL care coverage caps
      // at the HOME daily benefit, not the (typically non-qualified) IL daily
      // field that the old reimbursement carve-out zeroed. See
      // docs/il-payout-modeling-plan.md. The projection engine now routes IL
      // through the home benefit; `isIndependentLivingBenefitZeroedOut` remains
      // the source of truth for the legacy path and the client display shim.
      return resolve(ltcPolicy.dailyHomeBenefitAmount);
    default:
      return 0;
  }
}

export function getMonthlyBenefitAmountForEnvironment(
  ltcPolicy: PolicyFundingSource,
  careEnvironment: CareEnvironment,
): number {
  return getDailyBenefitAmountForEnvironment(ltcPolicy, careEnvironment) * DAYS_IN_MONTH;
}

export function getWaitingPeriodBucketForEnvironment(
  careEnvironment: CareEnvironment,
): 'homeDays' | 'facilityDays' {
  // Independent Living serves the HOME elimination period: its care is
  // home-equivalent and covered as home care (see il-payout-modeling-plan.md).
  return careEnvironment === CareEnvironment.home ||
    careEnvironment === CareEnvironment.independentLiving
    ? 'homeDays'
    : 'facilityDays';
}

// Maps a care environment to the environment whose per-environment policy
// benefit funds its CARE coverage. Independent Living care is home-equivalent
// (room & board is out-of-pocket, handled separately via
// `facts.roomAndBoardCost`), so IL maps to HOME and its care is covered off the
// home daily benefit. Every other environment maps to itself.
//
// Consequence: the policy's `dailyIndependentLivingBenefitAmount` field is no
// longer read for IL coverage (home is), so it is now effectively vestigial on
// the insurance forms. Removing that field from the forms is a planned
// follow-up — not done here to keep this change scoped. See
// docs/il-payout-modeling-plan.md.
//
// Pure env→env map — callers that need the RAW configured field (e.g. an
// explicit-zero check that deliberately skips the max-benefit fallback) must
// NOT route through this.
export function coverageEnvironmentFor(careEnvironment: CareEnvironment): CareEnvironment {
  return careEnvironment === CareEnvironment.independentLiving
    ? CareEnvironment.home
    : careEnvironment;
}

export interface WaitingPeriodConsumptionResult {
  openRelevantWp: number;
  closeRelevantWp: number;
  wpDaysThisRow: number;
  closeHomeWp: number;
  closeFacilityWp: number;
  waitingPeriodDaysConsumedHome: number;
  waitingPeriodDaysConsumedFacility: number;
}

export function consumeWaitingPeriodDaysForEnvironment(
  waitingPeriod: { homeDays: number; facilityDays: number },
  careEnvironment: CareEnvironment,
  daysAfter2Adl: number,
): WaitingPeriodConsumptionResult {
  const bucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openHomeWp = Math.max(waitingPeriod.homeDays, 0);
  const openFacilityWp = Math.max(waitingPeriod.facilityDays, 0);
  const openRelevantWp = bucket === 'homeDays' ? openHomeWp : openFacilityWp;
  const daysServed = Math.max(daysAfter2Adl, 0);

  // The elimination period is a single, claim-wide clock measured in
  // *qualified days of care* — every day meeting the 2-ADL benefit trigger
  // counts toward BOTH the home and facility EP buckets, regardless of the
  // setting the care is delivered in (each decrement clamped to its own
  // balance). This is the standard "days of qualified service in any setting"
  // EP definition: a qualified home-care day satisfies the facility EP too,
  // even when it is already being paid.
  //
  // Payment is a SEPARATE gate: care in a given row is unpaid only while that
  // row's active-environment bucket is still open (`wpDaysThisRow`, which
  // drives `eligibleDays`). Once the active environment's EP is satisfied the
  // row pays, but the qualified days keep crediting the other bucket. Consequences:
  //  - Equal WPs (home 90 / facility 90) satisfy together, so a later care-
  //    environment change never restarts the waiting period (unchanged).
  //  - Unequal WPs (home 60 / facility 90): after 60 days home pays, and the
  //    continued qualified home-care days keep burning down the facility
  //    bucket — it reaches 0 at day 90, so facility care beginning later pays
  //    immediately.
  //  - A 0-day environment (e.g. Asset Care's home EP) pays from day one, and
  //    those qualified home-care days still advance the facility bucket — the
  //    facility EP is satisfied by 90 qualified home-care days before any
  //    facility care begins.
  const wpDaysThisRow = Math.min(daysServed, openRelevantWp);
  const waitingPeriodDaysConsumedHome = Math.min(daysServed, openHomeWp);
  const waitingPeriodDaysConsumedFacility = Math.min(daysServed, openFacilityWp);
  const closeHomeWp = openHomeWp - waitingPeriodDaysConsumedHome;
  const closeFacilityWp = openFacilityWp - waitingPeriodDaysConsumedFacility;
  const closeRelevantWp = bucket === 'homeDays' ? closeHomeWp : closeFacilityWp;

  return {
    openRelevantWp,
    closeRelevantWp,
    wpDaysThisRow,
    closeHomeWp,
    closeFacilityWp,
    waitingPeriodDaysConsumedHome,
    waitingPeriodDaysConsumedFacility,
  };
}

// INDEPENDENT-satisfaction sibling of `consumeWaitingPeriodDaysForEnvironment`
// (same signature, same result shape). Here each environment's elimination
// period is its own clock: a qualified day of care decrements ONLY the active
// environment's bucket (per `getWaitingPeriodBucketForEnvironment` — so
// independent living still charges the facility bucket); the other bucket
// carries forward untouched. Contrast with the claim-wide helper above, where
// every qualified day burns BOTH buckets ("days of qualified service in any
// setting", the Asset Care-style definition): under claim-wide rules, 30
// qualified home-care days turn {home: 20, facility: 90} into {home: 0,
// facility: 60}; under this helper they leave {home: 0, facility: 90} — later
// facility care must still serve its full 90-day EP. Use this for products
// whose EPs are separately elected per coverage and satisfied independently,
// e.g. ManhattanLife OmniFlex STC (Q-EP default: satisfied independently per
// environment — see `docs/manhattanlife-omniflex/field-mapping.md`).
export function consumeWaitingPeriodDaysForEnvironmentIndependent(
  waitingPeriod: { homeDays: number; facilityDays: number },
  careEnvironment: CareEnvironment,
  daysAfter2Adl: number,
): WaitingPeriodConsumptionResult {
  const bucket = getWaitingPeriodBucketForEnvironment(careEnvironment);
  const openHomeWp = Math.max(waitingPeriod.homeDays, 0);
  const openFacilityWp = Math.max(waitingPeriod.facilityDays, 0);
  const openRelevantWp = bucket === 'homeDays' ? openHomeWp : openFacilityWp;
  const daysServed = Math.max(daysAfter2Adl, 0);

  const wpDaysThisRow = Math.min(daysServed, openRelevantWp);
  const waitingPeriodDaysConsumedHome = bucket === 'homeDays' ? wpDaysThisRow : 0;
  const waitingPeriodDaysConsumedFacility = bucket === 'facilityDays' ? wpDaysThisRow : 0;
  const closeHomeWp = openHomeWp - waitingPeriodDaysConsumedHome;
  const closeFacilityWp = openFacilityWp - waitingPeriodDaysConsumedFacility;
  const closeRelevantWp = bucket === 'homeDays' ? closeHomeWp : closeFacilityWp;

  return {
    openRelevantWp,
    closeRelevantWp,
    wpDaysThisRow,
    closeHomeWp,
    closeFacilityWp,
    waitingPeriodDaysConsumedHome,
    waitingPeriodDaysConsumedFacility,
  };
}

export function getRiderForm(ltcPolicy: PolicyFundingSource): CareMattersRiderForm {
  const capYearsRaw = ltcPolicy.policyInflationProtectionYears;
  return {
    rate: ltcPolicy.policyInflationProtection ?? 0,
    simple: ltcPolicy.isSimpleInflationProtection === true,
    capYears: capYearsRaw && capYearsRaw > 0 ? capYearsRaw : null,
  };
}

// Mirrors legacy `getCareMattersBenefitPeriodMonths`. Pre-PR CM rows lack
// `policyBenefitPeriodMonths`; fall back to the source quote so legacy
// rows still produce a non-zero base pool. Without this fallback the
// pool initializes to 0 and the strategy degenerates to rider-only.
export function getBenefitPeriodMonths(ltcPolicy: PolicyFundingSource): number {
  if (
    ltcPolicy.policyBenefitPeriodMonths !== null &&
    ltcPolicy.policyBenefitPeriodMonths !== undefined
  ) {
    return ltcPolicy.policyBenefitPeriodMonths;
  }
  const rawQuote: unknown = ltcPolicy.sourceQuote;
  if (!rawQuote) return 0;
  let quote: unknown = rawQuote;
  if (typeof quote === 'string') {
    try {
      quote = JSON.parse(quote);
    } catch {
      return 0;
    }
  }
  if (quote && typeof quote === 'object' && 'ltcBenefitDurationMonths' in quote) {
    const value = (quote as { ltcBenefitDurationMonths?: unknown }).ltcBenefitDurationMonths;
    return typeof value === 'number' ? value : 0;
  }
  return 0;
}

// Calendar-anchored policy year for a projection row. Anchors on the first
// phase's policy start year and advances strictly by `monthIndex / 12`, so
// the year increments on every 12th projection row regardless of phase
// boundaries. Use this for display + for any per-calendar-year accrual
// (e.g. Asset Flex ACIO, and rider escalation on products whose riders step
// on policy anniversaries — which, per the carrier illustrations, includes
// CM II since P2-1 retired its legacy paid-month rider clock).
//
// Assumes phases are contiguous in calendar time. Intentional calendar
// gaps between phases (rare) would undercount years here.
export function getCalendarPolicyYear(client: Client, monthIndex: number): number {
  const phaseCarePlans = client.projectionInputs?.phaseCarePlans ?? [];
  if (phaseCarePlans.length === 0) return 1;
  const firstProjectedCarePhase = client.projectionInputs?.monthlyCareInputs?.[0]?.carePhase;
  const anchorPhasePlan =
    (firstProjectedCarePhase != null
      ? phaseCarePlans.find(plan => plan.carePhase === firstProjectedCarePhase)
      : null) ?? phaseCarePlans[0];
  const firstPhasePolicyStartYear = Math.floor(
    getPhasePolicyStartYear(client, anchorPhasePlan),
  );
  return firstPhasePolicyStartYear + Math.floor(monthIndex / 12);
}

// Calendar policy year for a projection row. Single-life derives the anchor
// from the client's own first projected phase (`getCalendarPolicyYear`). Joint
// runs pass an explicit shared anchor — the policy year at monthIndex 0, i.e.
// the earliest claim start across both spouses — so both spouses sit on one
// monotonic policy-year clock (plan A3). The session client is not a reliable
// anchor for joint because the partner may claim first.
export function resolveRowPolicyYear(
  client: Client,
  monthIndex: number,
  jointAnchorPolicyYear: number | undefined,
): number {
  // Joint anchor + calendar offset, UNCLAMPED. `jointAnchorPolicyYear` is the
  // integer policy year at monthIndex 0 (= Jan 1 of the earliest spouse's onset
  // year; see `buildJointProjectionContext`), and `monthIndex` is measured from
  // that same Jan-1 origin, so `anchor + floor(monthIndex/12)` lands every row on
  // its true calendar policy year by integer math — no fractional-floor drift.
  // In-force rows are naturally >= 1; rows whose care predates the policy's
  // premium-start year resolve to <= 0 and are exactly the set the pre-policy
  // gate catches (those months pay $0 and book the full cost out-of-pocket). An
  // earlier `Math.max(1, …)` clamp here collapsed policy-year 0 and 1 onto "1",
  // which both masked pre-policy months and suppressed the first real
  // anniversary; the gate supersedes it. Single-life still floors via
  // `getPhasePolicyStartYear`'s own `Math.max(1, ...)`, so it cannot reach < 1.
  return jointAnchorPolicyYear != null
    ? jointAnchorPolicyYear + Math.floor(monthIndex / 12)
    : getCalendarPolicyYear(client, monthIndex);
}

// Anniversary-compound factor used by asset-based hybrid strategies whose
// pools and per-month cap inflate on every policy anniversary. `policyYear = 1`
// is the at-issue base (factor 1.0); the benefit then steps up on each policy
// anniversary. A `capYears`-year inflation rider applies `capYears` annual
// increases — matching carrier illustrations (e.g. OneAmerica Asset Care's
// "20 year period": the AOB/COB monthly cap grows through policy year 21 to
// `(1+rate)^20`, then freezes). So the exponent equals `policyYear - 1` until
// it caps at `capYears`, holding `(1+rate)^(capYears)` for every later year.
// `capYears <= 0` means uncapped.
export function compoundFactor(
  rate: number,
  capYears: number,
  policyYear: number,
): number {
  const rawExponent = Math.max(policyYear - 1, 0);
  const exponent = capYears > 0 ? Math.min(rawExponent, capYears) : rawExponent;
  return Math.pow(1 + rate, exponent);
}

// Growth factor honoring `isSimpleInflationProtection`. Compound defers to
// `compoundFactor`. Simple grows by a fixed `rate` step per anniversary, so
// the factor is the linear `1 + rate × steps` with the SAME cap convention as
// `compoundFactor` and legacy `calcInflationProtectionFutureValue`: a
// capYears-year rider applies capYears increases (steps = min(policyYear − 1,
// capYears)). This is the single source of truth for inflation growth —
// strategies must import it rather than carry local copies; the retired
// per-strategy `poolGrowthFactor` variants applied only capYears − 1 simple
// increases (the theme-2 off-by-one).
export function growthFactor(
  rate: number,
  capYears: number,
  isSimple: boolean,
  policyYear: number,
): number {
  if (!isSimple) return compoundFactor(rate, capYears, policyYear);
  const rawSteps = Math.max(policyYear - 1, 0);
  const steps = capYears > 0 ? Math.min(rawSteps, capYears) : rawSteps;
  return 1 + rate * steps;
}

// Care-cost inflation anchor ("today") for a client. Reads the
// `costInflationAnchorYear` the derivative pass pins to the actual current
// year (P1-7: Stage 1 and the projection engine must share one cost clock —
// this used to be the `clientAddedDateTime` year, which lags today for
// existing clients), with a current-year fallback for callers running
// outside the derivative pipeline. The whole app uses year-only granularity
// for this anchor — keep that consistent if you ever switch to fractional
// precision.
export function getCareCostAnchorYear(client: Client): number {
  return client.costInflationAnchorYear ?? new Date().getFullYear();
}

// Predicted calendar year of the first contributing phase (in care-phase
// order). Mirrors how `calcMonthlyCareInputs` starts the row stream at the
// first non-zero phase. Falls back to the cost anchor when no phase
// contributes months — keeps inflation at 0 instead of blowing up.
export function getFirstContributingPhaseStartYear(client: Client): number {
  const phasePlans = client.projectionInputs?.phaseCarePlans ?? [];
  const firstContributing = phasePlans.find(
    p => Math.round(p.phaseCareMonthsNeeded) > 0,
  );
  return (
    (firstContributing &&
      client.clientPhasePredictedStartYears?.[firstContributing.carePhase]) ??
    getCareCostAnchorYear(client)
  );
}

// Per-phase care duration map sourced from `client.projectionInputs.phaseCarePlans`.
// Surfaced on `ClaimTerms.phaseCareMonthsByPhase` so the projection-details UI
// can derive phase-scoped quantities (e.g. the <2 ADL bucket size) without
// having to plumb the raw client through. Phases the plan doesn't visit are
// absent (Partial), not zero-valued — distinguishes "no plan" from "plan with
// 0 months".
export function getPhaseCareMonthsByPhase(
  client: Client,
): Partial<Record<CarePhase, number>> {
  const plans = client.projectionInputs?.phaseCarePlans ?? [];
  const out: Partial<Record<CarePhase, number>> = {};
  for (const plan of plans) {
    out[plan.carePhase] = plan.phaseCareMonthsNeeded;
  }
  return out;
}

export function getPhasePolicyStartYear(
  client: Client,
  phaseCarePlan: PhaseCarePlan,
): number {
  const policyPremiumStartYear = client.fundingSources?.ltcPolicy?.policyPremiumStartYear;
  if (isNullOrUndefined(policyPremiumStartYear)) {
    throw new Error('Policy premium start year is not defined');
  }
  const carePhase = phaseCarePlan.carePhase;
  const startCalendarYear = client.clientPhasePredictedStartYears?.[carePhase];
  const endCalendarYear = client.clientPhasePredictedEndYears?.[carePhase];
  const phasePredictedStartYear =
    startCalendarYear ??
    Math.max(
      (endCalendarYear ?? policyPremiumStartYear) -
        Math.ceil(phaseCarePlan.phaseCareMonthsNeeded / 12),
      policyPremiumStartYear,
    );
  return Math.max(1, phasePredictedStartYear - policyPremiumStartYear + 1);
}
